import React, { PureComponent } from "react";
import { Link, withRouter } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import moment from "moment";
import {
  defaultOptions,
  checkStatus,
  parseJSON,
  getLocationOrigin,
} from "../../../services/fetchTools";
import {
  colors,
  shadows,
  card,
  btn,
  table,
  summary,
  empty,
  spinner,
} from "../../../style/common/partnerStyle";

class AnnouncementList extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      announcements: [],
      search_term: "",
    };
    this.onChangeSearch = this.onChangeSearch.bind(this);
  }

  componentDidMount() {
    this.getAnnouncements();
  }

  getAnnouncements() {
    const url = `${getLocationOrigin()}/notification/get_announcement_list/`;
    const options = { ...defaultOptions };
    return fetch(url, options)
      .then(checkStatus)
      .then(parseJSON)
      .then((data) => {
        this.setState({ announcements: data.data, loading: false });
      })
      .catch((error) => error);
  }

  onChangeSearch(e) {
    this.setState({ search_term: e.target.value });
  }

  getFiltered() {
    const { announcements, search_term } = this.state;
    const term = search_term.trim().toLowerCase();
    if (!term) return announcements;
    return announcements.filter((item) =>
      (item.title && item.title.toLowerCase().includes(term)) ||
      (item.created_by && item.created_by.toLowerCase().includes(term))
    );
  }

  renderSkeletonRows() {
    return Array.from({ length: 8 }).map((_, i) => (
      <tr key={"sk-" + i}>
        {[120, 220, 60, 160, 60, 100, 110].map((w, j) => (
          <td key={j} style={table.td}>
            <div style={{
              width: w,
              height: 14,
              borderRadius: 4,
              background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: `shimmer 1.2s infinite`,
              animationDelay: `${i * 0.06}s`,
            }} />
          </td>
        ))}
      </tr>
    ));
  }

  render() {
    const { loading, search_term } = this.state;
    const filtered = this.getFiltered();

    return (
      <div style={styles.page}>
        {/* ── Header ── */}
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.pageTitle}>Announcements</h1>
            {!loading && (
              <p style={styles.pageSub}>
                {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
              </p>
            )}
          </div>
          <div style={styles.headerActions}>
            {/* Search */}
            <div style={styles.searchWrap}>
              <svg style={styles.searchIcon} viewBox="0 0 18 18" fill="none">
                <circle cx="7.5" cy="7.5" r="5" stroke={colors.muted} strokeWidth="1.4" />
                <path d="M11.5 11.5l3 3" stroke={colors.muted} strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search by title or sender…"
                value={search_term}
                onChange={this.onChangeSearch}
              />
            </div>
            <Link to="./sendAnnouncements">
              <button style={btn.primary}>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                New
              </button>
            </Link>
          </div>
        </div>

        {/* ── Table ── */}
        <div style={card.wrap}>
          <div style={table.wrap}>
            <table style={table.table}>
              <thead>
                <tr>
                  {["Date", "Title", "Reach", "Cities / Venues", "Installs", "Read Rate", "Sent By"].map((h) => (
                    <th key={h} style={table.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  this.renderSkeletonRows()
                ) : filtered.length > 0 ? (
                  filtered.map((item, idx) => {
                    const readPct =
                      item.recepients.total_count > 0
                        ? Math.round((item.recepients.read_count / item.recepients.total_count) * 100)
                        : 0;

                    const barColor =
                      readPct >= 60 ? colors.success :
                      readPct >= 30 ? colors.amber :
                      colors.danger;

                    const cityText =
                      item.cities.length < 5
                        ? item.cities.map((c) => c.name).join(", ")
                        : item.cities.length + " Cities";
                    const cityTip =
                      item.cities.length >= 5
                        ? item.cities.map((c) => c.name).join(", ")
                        : null;

                    const venueText =
                      item.venues.length < 5
                        ? item.venues.map((v) => v.name).join(", ")
                        : item.venues.length + " Venues";
                    const venueTip =
                      item.venues.length >= 5
                        ? item.venues.map((v) => v.name).join(", ")
                        : null;

                    return (
                      <tr
                        key={item.id}
                        style={{
                          ...(idx % 2 !== 0 ? table.trAlt : {}),
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = colors.brandLt; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 !== 0 ? colors.altRow : ""; }}
                        onClick={() => {
                          this.props.history.push(`/announcements/${item.id}`, { announcement: item });
                        }}
                      >
                        <td style={{ ...table.td, color: colors.muted, fontSize: 12 }}>
                          {moment(item.created_on).format("DD MMM YYYY")}
                        </td>
                        <td style={table.tdBold}>{item.title}</td>
                        <td style={table.td}>{item.recepients.total_count.toLocaleString()}</td>
                        <td style={{ ...table.td, maxWidth: 200 }}>
                          {cityTip ? (
                            <span data-tip={cityTip} style={styles.tag}>{cityText}</span>
                          ) : (
                            <span style={{ color: colors.text, fontSize: 13 }}>{cityText}</span>
                          )}
                          {cityText && venueText && <span style={{ color: colors.border, margin: "0 4px" }}>·</span>}
                          {venueTip ? (
                            <span data-tip={venueTip} style={styles.tag}>{venueText}</span>
                          ) : (
                            <span style={{ color: colors.text, fontSize: 13 }}>{venueText}</span>
                          )}
                          <ReactTooltip place="bottom" type="dark" effect="solid" />
                        </td>
                        <td style={table.td}>{item.recepients.app_installed.toLocaleString()}</td>
                        <td style={table.td}>
                          <div style={styles.barRow}>
                            <div style={styles.barTrack}>
                              <div style={{ ...styles.barFill, width: readPct + "%", background: barColor }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: barColor, minWidth: 32 }}>
                              {readPct}%
                            </span>
                          </div>
                        </td>
                        <td style={table.td}>
                          <div style={styles.senderRow}>
                            <div style={styles.avatar}>
                              {item.created_by ? item.created_by.charAt(0).toUpperCase() : "?"}
                            </div>
                            <span style={{ fontSize: 13, color: colors.text }}>{item.created_by}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7">
                      <div style={empty.wrap}>
                        <div style={empty.icon}>📢</div>
                        <div style={empty.title}>
                          {search_term ? `No results for "${search_term}"` : "No announcements yet"}
                        </div>
                        <div style={empty.text}>
                          {search_term ? "Try a different search term." : "Create your first announcement to get started."}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }
}

const styles = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: "24px 20px",
    boxSizing: "border-box",
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: colors.text,
    margin: 0,
  },
  pageSub: {
    fontSize: 13,
    color: colors.muted,
    margin: "4px 0 0 0",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 10,
    width: 16,
    height: 16,
    pointerEvents: "none",
  },
  searchInput: {
    padding: "8px 12px 8px 34px",
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
    color: colors.text,
    width: 240,
    background: colors.surface,
  },
  tag: {
    display: "inline-block",
    background: colors.brandLt,
    color: colors.brand,
    borderRadius: 6,
    padding: "2px 8px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "default",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  barTrack: {
    flex: 1,
    height: 6,
    background: colors.border,
    borderRadius: 99,
    overflow: "hidden",
    minWidth: 60,
  },
  barFill: {
    height: "100%",
    borderRadius: 99,
    transition: "width 0.4s ease",
  },
  senderRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: colors.brandLt,
    color: colors.brand,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
};

export default withRouter(AnnouncementList);


// WEBPACK FOOTER //
// ./src/app/views/partner/announcements/announcementList.js